/**
 * Geometria exata de cada imagem do STAR, a partir da calibração do georef
 * (`src/config/georef.json`):
 *
 * - A origem (canto superior esquerdo) na grade ABI de 1 km varia um pouco
 *   por grupo de produtos e por tamanho (`gridOrigins`). Fórmula do georef:
 *     xMin = -0.151872 + col·p;  yMax = 0.151872 − row·p;
 *     xMax = xMin + 7200·p;      yMin = yMax − 4320·p     (p = pixelSizeRad)
 *   Produto/tamanho sem calibração → extensão geral (xMin…yMax).
 * - A faixa de rodapé (data e créditos gravados pela NOAA) ocupa uma fração
 *   da altura que depende do tamanho (`decorationsBySize[w].footerFraction`).
 *
 * `NSA_EXTENT` (projection.ts) continua sendo a extensão geral da vista.
 */
import type { Extent } from 'ol/extent';
import georefJson from '../config/georef.json';
import type { ImageWidth } from '../goes/star';
import { NSA_EXTENT, SAT_HEIGHT } from './projection';

interface GridGroup {
  products?: string[];
  [width: string]: unknown;
}

interface GeorefShape {
  pixelSizeRad?: number;
  gridOrigins?: Record<string, GridGroup>;
  footerFraction?: number;
  decorationsBySize?: Record<string, { footerPx?: number; footerFraction?: number }>;
}

// Leitura defensiva: o georef pode acrescentar/alterar campos.
const georef = georefJson as unknown as GeorefShape;

/** Grade ABI de 1 km: 10848 × 10848 pixels, bordas em ±5424·p (= ±0,151872 rad). */
const GRID_HALF = 10848 / 2;

function originFor(productId: string, width: ImageWidth): [number, number] | null {
  const groups = georef.gridOrigins;
  if (!groups) return null;
  for (const group of Object.values(groups)) {
    if (!group?.products?.includes(productId)) continue;
    const o = group[String(width)];
    if (Array.isArray(o) && o.length === 2 && o.every((n) => typeof n === 'number' && Number.isFinite(n))) {
      return [o[0] as number, o[1] as number];
    }
    return null;
  }
  return null;
}

const extentCache = new Map<string, Extent>();

/**
 * Larguras acima das do STAR: o nível de 14400 px dos blocos (banda 02 a
 * 0,5 km, detalhe/blocos.ts) é a grade de 7200 com 2×2 px por px — a mesma
 * extensão e a mesma faixa de rodapé.
 */
export type GeoWidth = ImageWidth | 14400;
const asStarWidth = (width: GeoWidth): ImageWidth => (width > 7200 ? 7200 : (width as ImageWidth));

/** Extensão (bordas externas, unidades do mapa) da imagem do produto nesse tamanho. */
export function imageExtentFor(productId: string, geoWidth: GeoWidth): Extent {
  const width = asStarWidth(geoWidth);
  const key = `${productId}|${width}`;
  let ext = extentCache.get(key);
  if (!ext) {
    const p = georef.pixelSizeRad;
    const origin = originFor(productId, width);
    if (p && origin) {
      const xMin = (-GRID_HALF + origin[0]) * p;
      const yMax = (GRID_HALF - origin[1]) * p;
      ext = [xMin * SAT_HEIGHT, (yMax - 4320 * p) * SAT_HEIGHT, (xMin + 7200 * p) * SAT_HEIGHT, yMax * SAT_HEIGHT];
    } else {
      ext = NSA_EXTENT;
    }
    extentCache.set(key, ext);
  }
  return ext;
}

/** Mesmo nome usado em docs/georef.md. */
export const goesExtent = imageExtentFor;

/** Fração da altura da imagem ocupada pela faixa de rodapé da NOAA nesse tamanho. */
export function footerFractionFor(geoWidth: GeoWidth): number {
  const width = asStarWidth(geoWidth);
  const d = georef.decorationsBySize?.[String(width)];
  if (d?.footerFraction !== undefined && Number.isFinite(d.footerFraction)) return d.footerFraction;
  if (d?.footerPx !== undefined && Number.isFinite(d.footerPx)) return d.footerPx / ((width * 3) / 5);
  return georef.footerFraction ?? 0;
}

/**
 * Recorte da camada para esconder a faixa de rodapé (sem alterar a imagem):
 * a mesma extensão com a borda de baixo elevada pela altura do rodapé.
 */
export function footerClipExtent(extent: Extent, width: GeoWidth): Extent {
  const f = footerFractionFor(width);
  return [extent[0], extent[1] + f * (extent[3] - extent[1]), extent[2], extent[3]];
}
